import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Info, Inbox as InboxIcon, Plug, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, Field, SelectField, Stat } from "@/components/crm/parts";
import { InboxSuggestionRow } from "@/components/crm/InboxSuggestionRow";
import { supabase } from "@/integrations/supabase/client";
import { useCrmInvalidate } from "@/lib/crm/useCrm";
import { useInboxDemoFeed } from "@/lib/crm/useInboxDemo";
import {
  ACTIVITY_KIND,
  INBOX_PROVIDERS,
  type InboxSuggestion,
  type SuggestionKind,
} from "@/lib/crm/inbox-demo";

export const Route = createFileRoute("/_authenticated/sales/inbox")({
  head: () => ({
    meta: [
      { title: "Inbox & messaging capture — MyPriorities" },
      {
        name: "description",
        content:
          "Preview how Gmail, Outlook, Slack and Teams turn emails, meetings and messages into customer engagement records you review before logging.",
      },
      { property: "og:title", content: "Inbox & messaging capture — MyPriorities" },
      {
        property: "og:description",
        content:
          "Review suggested touchpoints from Gmail, Outlook, Slack and Teams, then log them against the right customer account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: InboxPage,
});

function InboxPage() {
  return (
    <SalesShell
      section="customers"
      title="Inbox & messaging"
      subtitle="Turn emails, meetings and messages into engagement records — you review every suggestion before anything is logged."
    >
      {({ orgId, userId }) => <InboxCentre orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

const KIND_OPTIONS: { value: SuggestionKind; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "message", label: "Message (Slack / Teams)" },
  { value: "call", label: "Call" },
];

const KIND_LABEL = new Map(KIND_OPTIONS.map((o) => [o.value, o.label]));

function InboxCentre({ orgId, userId }: { orgId: string; userId: string }) {
  const feed = useInboxDemoFeed(orgId, userId);
  const invalidate = useCrmInvalidate();
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<InboxSuggestion | null>(null);
  const [busy, setBusy] = useState(false);

  const pendingIds = useMemo(() => feed.pending.map((s) => s.id), [feed.pending]);
  const selectable = useMemo(
    () => feed.pending.filter((s) => s.confidence !== "unmatched").map((s) => s.id),
    [feed.pending],
  );
  const activeSelection = selected.filter((id) => pendingIds.includes(id));

  const loggedCount = Object.values(feed.resolved).filter((r) => r === "logged").length;
  const dismissedCount = Object.values(feed.resolved).filter((r) => r === "dismissed").length;

  async function logSuggestions(items: InboxSuggestion[], override?: { note: string; on: string }) {
    const rows = items
      .filter((s) => s.accountId)
      .map((s) => {
        const label = KIND_LABEL.get(s.kind) ?? "Contact";
        const on = override?.on ?? s.occurredOn;
        const note = override?.note?.trim() || s.subject;
        return {
          organisation_id: orgId,
          owner_user_id: userId,
          record_kind: "account",
          record_id: s.accountId!,
          kind: ACTIVITY_KIND[s.kind],
          summary: `${label} on ${on} — ${note} (captured from ${
            INBOX_PROVIDERS.find((p) => p.id === s.provider)?.label ?? s.provider
          })`,
        };
      });
    if (!rows.length) return;

    setBusy(true);
    const { error } = await supabase.from("crm_activity").insert(rows);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    feed.resolve(
      items.map((s) => s.id),
      "logged",
    );
    setSelected([]);
    invalidate();
    toast.success(rows.length === 1 ? "Touchpoint logged" : `${rows.length} touchpoints logged`);
  }

  function dismiss(items: InboxSuggestion[]) {
    feed.resolve(
      items.map((s) => s.id),
      "dismissed",
    );
    setSelected([]);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-4">
        <Info className="mt-0.5 h-4 w-4 text-muted-foreground" />
        <div className="text-sm">
          <p className="font-medium">Preview of automated capture</p>
          <p className="text-muted-foreground">
            Live mailbox and message sync is coming for Enterprise. Switch a provider on below to see
            how captured touchpoints arrive for review — the samples are built from your own accounts
            and contacts, and nothing leaves MyPriorities.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {INBOX_PROVIDERS.map((provider) => {
          const on = feed.connected.includes(provider.id);
          return (
            <div key={provider.id} className="rounded-lg border border-border bg-card p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Plug className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">{provider.label}</p>
                </div>
                <Switch
                  checked={on}
                  onCheckedChange={() => feed.toggleProvider(provider.id)}
                  aria-label={`Preview ${provider.label} capture`}
                />
              </div>
              <p className="text-sm text-muted-foreground">{provider.blurb}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {provider.captures.map((capture) => (
                  <Badge key={capture} variant="outline">
                    {capture}
                  </Badge>
                ))}
                <Badge variant={on ? "secondary" : "outline"}>
                  {on ? "Connected (preview)" : "Not connected"}
                </Badge>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Waiting for review" value={String(feed.pending.length)} />
        <Stat label="Logged from inbox" value={String(loggedCount)} hint="Now in engagement history" />
        <Stat label="Dismissed" value={String(dismissedCount)} />
      </div>

      {!feed.hasAccounts ? (
        <EmptyState
          title="Add customer accounts first"
          body="Captured emails and messages are matched to accounts, so add a few in Customers → Accounts to see this in action."
        />
      ) : !feed.connected.length ? (
        <EmptyState
          title="No providers switched on"
          body="Turn on Gmail, Outlook, Slack or Teams above to preview the review inbox."
        />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4 text-muted-foreground" />
              <p className="font-medium">Review inbox ({feed.pending.length})</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setSelected(activeSelection.length === selectable.length ? [] : selectable)
                }
                disabled={!selectable.length}
              >
                {activeSelection.length === selectable.length && selectable.length
                  ? "Clear selection"
                  : "Select all matched"}
              </Button>
              <Button
                size="sm"
                disabled={busy || !activeSelection.length}
                onClick={() =>
                  void logSuggestions(feed.pending.filter((s) => activeSelection.includes(s.id)))
                }
              >
                Log selected ({activeSelection.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!activeSelection.length}
                onClick={() => dismiss(feed.pending.filter((s) => activeSelection.includes(s.id)))}
              >
                Dismiss selected
              </Button>
              {loggedCount + dismissedCount > 0 ? (
                <Button size="sm" variant="ghost" onClick={feed.reset}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Restore samples
                </Button>
              ) : null}
            </div>
          </div>

          {feed.pending.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              Everything has been reviewed. Use “Restore samples” to run through the demo again.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {feed.pending.map((suggestion) => (
                <InboxSuggestionRow
                  key={suggestion.id}
                  suggestion={suggestion}
                  selected={activeSelection.includes(suggestion.id)}
                  busy={busy}
                  onSelect={(checked) =>
                    setSelected((prev) =>
                      checked
                        ? [...prev.filter((id) => id !== suggestion.id), suggestion.id]
                        : prev.filter((id) => id !== suggestion.id),
                    )
                  }
                  onLog={() => void logSuggestions([suggestion])}
                  onEdit={() => setEditing(suggestion)}
                  onDismiss={() => dismiss([suggestion])}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <EditSuggestionDialog
        suggestion={editing}
        busy={busy}
        onClose={() => setEditing(null)}
        onSave={async (values) => {
          if (!editing) return;
          await logSuggestions([{ ...editing, kind: values.kind }], {
            note: values.note,
            on: values.on,
          });
          setEditing(null);
        }}
      />
    </div>
  );
}

function EditSuggestionDialog({
  suggestion,
  busy,
  onClose,
  onSave,
}: {
  suggestion: InboxSuggestion | null;
  busy: boolean;
  onClose: () => void;
  onSave: (values: { kind: SuggestionKind; on: string; note: string }) => Promise<void>;
}) {
  const [kind, setKind] = useState<SuggestionKind>("email");
  const [on, setOn] = useState("");
  const [note, setNote] = useState("");
  const [seeded, setSeeded] = useState<string | null>(null);

  if (suggestion && seeded !== suggestion.id) {
    setSeeded(suggestion.id);
    setKind(suggestion.kind);
    setOn(suggestion.occurredOn);
    setNote(suggestion.subject);
  }

  return (
    <Dialog open={Boolean(suggestion)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Log touchpoint{suggestion?.accountName ? ` — ${suggestion.accountName}` : ""}
          </DialogTitle>
          <DialogDescription>
            Adjust the details before this is added to the account's engagement history.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <SelectField
            label="Type"
            value={kind}
            onChange={(value) => setKind(value as SuggestionKind)}
            options={KIND_OPTIONS}
          />
          <Field label="Date">
            <Input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
          </Field>
          <Field label="Summary" hint="What was discussed or agreed.">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void onSave({ kind, on, note })} disabled={busy}>
            {busy ? "Saving…" : "Log touchpoint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
